import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertTriangle, Package, FileX, ArrowRight, FolderOpen } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useState, useEffect } from "react";

interface DeleteConfirmationDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (forceDelete?: boolean) => void;
  applicationId: number | null;
  applicationName: string;
  isDeleting?: boolean;
}

export function DeleteConfirmationDialog({
  isOpen,
  onOpenChange,
  onConfirm,
  applicationId,
  applicationName,
  isDeleting = false
}: DeleteConfirmationDialogProps) {
  const [forceDelete, setForceDelete] = useState(false);
  
  // Reset forceDelete state when dialog opens or closes
  useEffect(() => {
    if (!isOpen) {
      setForceDelete(false);
    }
  }, [isOpen]);
  
  // Fetch license count for the application
  const { data: licenseData, isLoading: isLoadingLicenses } = useQuery({
    queryKey: ['/api/applications', applicationId, 'license-count'],
    queryFn: async () => {
      if (!applicationId) return { count: 0 };
      const response = await fetch(`/api/applications/${applicationId}/license-count`);
      if (!response.ok) throw new Error('Failed to fetch license count');
      return response.json();
    },
    enabled: isOpen && applicationId !== null,
  });

  const licenseCount = licenseData?.count || 0;
  const hasLicenses = licenseCount > 0;

  const handleDelete = () => {
    onConfirm(forceDelete);
  };

  const handleCancel = () => {
    onOpenChange(false);
    setForceDelete(false); // Reset force delete state when canceling
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            Delete Application
          </DialogTitle>
          <DialogDescription>
            Are you sure you want to delete <strong>"{applicationName}"</strong>? 
            This action cannot be undone.
          </DialogDescription>
        </DialogHeader>
        
        {/* License Count Card */}
        <Card className={`border ${hasLicenses ? 'border-amber-200 bg-amber-50/50' : 'border-green-200 bg-green-50/50'}`}>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              {isLoadingLicenses ? (
                <>
                  <Skeleton className="h-10 w-10 rounded" />
                  <div className="flex-1">
                    <Skeleton className="h-4 w-32 mb-2" />
                    <Skeleton className="h-3 w-48" />
                  </div>
                </>
              ) : (
                <>
                  <div className={`p-2 rounded-lg ${hasLicenses ? 'bg-amber-100 text-amber-600' : 'bg-green-100 text-green-600'}`}>
                    {hasLicenses ? <Package className="h-6 w-6" /> : <FileX className="h-6 w-6" />}
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-sm">
                      {hasLicenses ? (
                        `${licenseCount} License${licenseCount !== 1 ? 's' : ''} Associated`
                      ) : (
                        'No Licenses Found'
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {hasLicenses ? (
                        forceDelete 
                          ? 'Licenses will be moved to "Uncategorized" application'
                          : 'Cannot delete application with active licenses'
                      ) : (
                        'Safe to delete without affecting licenses'
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Force Delete Option - only show when there are licenses */}
        {hasLicenses && !isLoadingLicenses && (
          <Card className="border-blue-200 bg-blue-50/50">
            <CardContent className="pt-4">
              <div className="flex items-start gap-3">
                <Checkbox 
                  id="force-delete"
                  checked={forceDelete}
                  onCheckedChange={(checked) => setForceDelete(!!checked)}
                  data-testid="checkbox-force-delete"
                />
                <div className="flex-1">
                  <label htmlFor="force-delete" className="text-sm font-medium cursor-pointer">
                    Force delete and transfer licenses
                  </label>
                  <div className="text-xs text-muted-foreground mt-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span>"{applicationName}"</span>
                      <ArrowRight className="h-3 w-3" />
                      <FolderOpen className="h-3 w-3" />
                      <span>"Uncategorized"</span>
                    </div>
                    <p>Licenses will be safely moved to an "Uncategorized" application so you won't lose any data.</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        
        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={isDeleting}
            data-testid="button-cancel-delete"
          >
            Cancel
          </Button>
          <Button 
            variant="destructive"
            onClick={handleDelete}
            disabled={isDeleting || (hasLicenses && !forceDelete)}
            data-testid="button-confirm-delete"
          >
            {isDeleting ? "Deleting..." : 
             hasLicenses && forceDelete ? "Delete & Transfer Licenses" :
             hasLicenses ? "Cannot Delete" : "Delete Application"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}